import {
  CloudWatchLogsClient,
  PutLogEventsCommand,
} from '@aws-sdk/client-cloudwatch-logs'

const ENV = process.env.REACT_APP_ENVIRONMENT || 'staging'
const ACCESS_KEY = process.env.REACT_APP_AWS_ACCESS_KEY
const SECRET_KEY = process.env.REACT_APP_AWS_SECRET_KEY
const REGION = process.env.REACT_APP_AWS_REGION || 'us-west-1'
const LOG_GROUP = 'express-checkin-amplify'
const FLUSH_INTERVAL_MS = 2000
const MAX_BUFFER = 100

type LogLevel = 'info' | 'event' | 'error'

interface ReservationContext {
  reservationId?: string
  firstName?: string
  lastName?: string
  email?: string
  hotelCode?: string
}

let client: CloudWatchLogsClient | null = null
let logStreamName = ''
let streamReady = false
let reservationCtx: ReservationContext = {}
let buffer: { timestamp: number; message: string }[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

export function setReservationContext(data: ReservationContext): void {
  reservationCtx = { ...data }
}

function ensureLogGroupAndStream(): void {
  logStreamName = ENV
  streamReady = true
}

async function flush(): Promise<void> {
  if (!client || !streamReady || buffer.length === 0) return
  const events = buffer.splice(0, MAX_BUFFER)
  try {
    await client.send(
      new PutLogEventsCommand({ logGroupName: LOG_GROUP, logStreamName, logEvents: events })
    )
  } catch (e) {
    buffer.unshift(...events)
  }
}

function scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flush()
  }, FLUSH_INTERVAL_MS)
}

function addLog(level: LogLevel, message: string, data?: Record<string, unknown>): void {
  if (!client) return
  if (buffer.length >= MAX_BUFFER * 2) return
  const entry = JSON.stringify({
    level,
    message,
    env: ENV,
    ts: new Date().toISOString(),
    ...reservationCtx,
    ...data,
  })
  buffer.push({ timestamp: Date.now(), message: entry })
  scheduleFlush()
}

function stringify(arg: unknown): string {
  if (typeof arg === 'object' && arg !== null) {
    try { return JSON.stringify(arg) } catch { return String(arg) }
  }
  return String(arg)
}

function patchConsole(): void {
  const origLog = console.log
  const origError = console.error
  console.log = (...args: unknown[]) => {
    origLog(...args)
    addLog('info', args.map(stringify).join(' '))
  }
  console.error = (...args: unknown[]) => {
    origError(...args)
    addLog('error', args.map(stringify).join(' '))
  }
}

export function initLogger(): void {
  if (!ACCESS_KEY || !SECRET_KEY) return
  client = new CloudWatchLogsClient({
    region: REGION,
    credentials: { accessKeyId: ACCESS_KEY, secretAccessKey: SECRET_KEY },
  })
  ensureLogGroupAndStream()
  patchConsole()
  window.addEventListener('beforeunload', () => void flush())
}

export function logEvent(name: string, data?: Record<string, unknown>): void {
  addLog('event', name, data)
}

export function logError(message: string, context?: Record<string, unknown>): void {
  console.error(message, context)
}
